import React, { useEffect, useState } from 'react';
import { Card, Col, Row, Skeleton, Tag, Typography } from 'antd';
import {
  BarChartOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  LockOutlined,
  MailOutlined,
  SolutionOutlined,
  ThunderboltOutlined,
  UsergroupAddOutlined,
  TeamOutlined,
  TrophyOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

const BRAND_INDIGO = '#2E2B81';
const BRAND_INDIGO_DEEP = '#1e1b6e';
const BRAND_GREEN = '#00984A';
const BRAND_YELLOW = '#FFC200';

const ROLE_LABELS: Record<string, { text: string; color: string }> = {
  ADMINISTRATOR: { text: 'Administrador', color: 'red' },
  CONTRACTOR: { text: 'Contratante', color: 'blue' },
  PARTICIPANT: { text: 'Participante', color: 'green' },
};

const ADMIN_LINKS = [
  { title: 'Gerenciar Pesquisas', path: '/admin/surveys', icon: <FileTextOutlined /> },
  { title: 'Ciclos de Engajamento', path: '/admin/engagement/cycles', icon: <TrophyOutlined /> },
  { title: 'Contratantes', path: '/admin/contractors', icon: <TeamOutlined /> },
  { title: 'Analytics', path: '/admin/', icon: <BarChartOutlined /> },
  { title: 'Regulamento dos Sorteios', path: '/admin/engagement/regulation', icon: <SolutionOutlined /> },
  { title: 'Documentos Legais', path: '/admin/legal', icon: <FileTextOutlined /> },
  { title: 'Usuários', path: '/admin/users', icon: <UserOutlined /> },
  { title: 'Comunicação em Massa', path: '/admin/bulk-email', icon: <MailOutlined /> },
];

const CONTRACTOR_LINKS = [
  { title: 'Minhas Pesquisas', path: '/contractor/surveys', icon: <FileTextOutlined /> },
  { title: 'Relatórios', path: '/contractor/reports', icon: <BarChartOutlined /> },
];

const PARTICIPANT_SLIDES = [
  {
    icon: <LockOutlined />,
    iconColor: BRAND_GREEN,
    title: 'Suas respostas são sempre anônimas',
    description: 'Nenhum identificador é vinculado à sua opinião. Fale com liberdade, sem exposição.',
  },
  {
    icon: <ThunderboltOutlined />,
    iconColor: 'rgba(255,255,255,0.90)',
    title: 'Rápido de fazer, real no impacto',
    description: 'A maioria das pesquisas leva menos de 5 minutos. Cada resposta contribui para um retrato fiel do que a sociedade pensa.',
  },
  {
    icon: <TrophyOutlined />,
    iconColor: BRAND_YELLOW,
    title: 'Responda e acumule pontos',
    description: 'Cada pesquisa respondida gera pontos no ciclo de engajamento. Concorra a prêmios.',
  },
  {
    icon: <UsergroupAddOutlined />,
    iconColor: '#7dd3fc',
    title: 'Indique amigos e ganhe pontos extras',
    description: 'Compartilhe seu link de indicação. A cada novo participante que se cadastrar, você acumula pontos adicionais.',
  },
];

const SLIDE_INTERVAL_MS = 6000;

interface LoggedInHeroMessageProps {
  answeredCount?: number;
  totalActiveCount?: number;
  cyclePoints?: number;
  qualifiedReferrals90d?: number;
  isLoading?: boolean;
}

export const LoggedInHeroMessage: React.FC<LoggedInHeroMessageProps> = ({
  answeredCount,
  totalActiveCount,
  cyclePoints,
  qualifiedReferrals90d,
  isLoading = false,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const userRole = user?.role || 'PARTICIPANT';
  const roleLabel = ROLE_LABELS[userRole] ?? { text: 'Usuário', color: 'default' };
  const quickLinks = userRole === 'ADMINISTRATOR' ? ADMIN_LINKS : CONTRACTOR_LINKS;
  const isParticipant = userRole === 'PARTICIPANT';

  const [activeSlide, setActiveSlide] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (!isParticipant || paused) return;
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % PARTICIPANT_SLIDES.length);
    }, SLIDE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [isParticipant, paused]);

  const hasStats =
    (totalActiveCount !== undefined && totalActiveCount > 0) ||
    cyclePoints !== undefined ||
    qualifiedReferrals90d !== undefined;

  return (
    <>
      <div
        style={{
          background: `linear-gradient(135deg, ${BRAND_INDIGO} 0%, ${BRAND_INDIGO_DEEP} 100%)`,
          padding: '28px 24px 24px',
          textAlign: 'center',
        }}
      >
        {isParticipant ? (
          <>
            {/* Carousel slides */}
            <div
              style={{ position: 'relative', height: 120, overflow: 'hidden' }}
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
            >
              {PARTICIPANT_SLIDES.map((slide, i) => (
                <div
                  key={i}
                  aria-hidden={i !== activeSlide}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: i === activeSlide ? 1 : 0,
                    transition: 'opacity 0.7s ease',
                    padding: '0 16px',
                  }}
                >
                  <div style={{ fontSize: 28, color: slide.iconColor, marginBottom: 10, lineHeight: 1 }}>
                    {slide.icon}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#ffffff', marginBottom: 6, lineHeight: 1.3 }}>
                    {slide.title}
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.72)', maxWidth: 380, lineHeight: 1.55 }}>
                    {slide.description}
                  </div>
                </div>
              ))}
            </div>

            {/* Dot indicators */}
            <div
              role="tablist"
              aria-label="Slides do carrossel"
              style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 14 }}
            >
              {PARTICIPANT_SLIDES.map((_, i) => (
                <button
                  key={i}
                  role="tab"
                  aria-selected={i === activeSlide}
                  aria-label={`Slide ${i + 1}`}
                  onClick={() => { setActiveSlide(i); setPaused(false); }}
                  style={{
                    width: i === activeSlide ? 20 : 8,
                    height: 8,
                    borderRadius: 4,
                    background: i === activeSlide ? '#ffffff' : 'rgba(255,255,255,0.35)',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    transition: 'all 0.3s ease',
                  }}
                />
              ))}
            </div>

            {/* Progress pills */}
            <div style={{ marginTop: 16 }}>
              {isLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Skeleton.Input active style={{ width: 320, height: 30, borderRadius: 20 }} />
                </div>
              ) : hasStats ? (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {totalActiveCount !== undefined && totalActiveCount > 0 && (
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '5px 14px',
                      background: answeredCount === totalActiveCount
                        ? 'rgba(0,152,74,0.25)'
                        : 'rgba(255,255,255,0.12)',
                      borderRadius: 20, fontSize: 13, color: 'rgba(255,255,255,0.85)',
                    }}>
                      <CheckCircleOutlined style={{ color: BRAND_GREEN, fontSize: 13 }} />
                      <span style={{ fontWeight: 600, color: '#ffffff' }}>
                        {answeredCount ?? 0}/{totalActiveCount}
                      </span>
                      {' '}respondidas
                    </div>
                  )}
                  {cyclePoints !== undefined && (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate('/engagement')}
                      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate('/engagement')}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '5px 14px', background: 'rgba(255,255,255,0.12)',
                        borderRadius: 20, fontSize: 13, color: 'rgba(255,255,255,0.85)',
                        cursor: 'pointer', transition: 'background 0.15s ease',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.22)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.12)')}
                    >
                      <TrophyOutlined style={{ color: BRAND_YELLOW, fontSize: 13 }} />
                      <span style={{ fontWeight: 600, color: '#ffffff' }}>{cyclePoints}</span>
                      {' '}pontos neste ciclo
                    </div>
                  )}
                  {qualifiedReferrals90d !== undefined && (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate('/engagement#referrals')}
                      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate('/engagement#referrals')}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '5px 14px', background: 'rgba(255,255,255,0.12)',
                        borderRadius: 20, fontSize: 13, color: 'rgba(255,255,255,0.85)',
                        cursor: 'pointer', transition: 'background 0.15s ease',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.22)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.12)')}
                    >
                      <UsergroupAddOutlined style={{ color: '#7dd3fc', fontSize: 13 }} />
                      <span style={{ fontWeight: 600, color: '#ffffff' }}>{qualifiedReferrals90d}</span>
                      {' '}indicações (90 dias)
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </>
        ) : (
          /* Role tag — admin / contractor */
          <div>
            <Tag color={roleLabel.color}>{roleLabel.text}</Tag>
          </div>
        )}

        {/* Accent bar */}
        <div
          style={{
            height: 4,
            width: 80,
            margin: '20px auto 0',
            background: `linear-gradient(90deg, ${BRAND_GREEN}, ${BRAND_YELLOW})`,
            borderRadius: 2,
          }}
        />
      </div>

      {/* Quick links (admin / contractor) */}
      {(userRole === 'ADMINISTRATOR' || userRole === 'CONTRACTOR') && (() => {
        const count = quickLinks.length;
        const lgSpan = count <= 2 ? 12 : count === 3 ? 8 : 6;
        const smSpan = count === 1 ? 24 : 12;
        return (
          <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 24px 32px' }}>
            <Row gutter={[12, 12]}>
              {quickLinks.map((item) => (
                <Col xs={24} sm={smSpan} md={smSpan} lg={lgSpan} xl={lgSpan} key={item.path}>
                  <Card
                    hoverable
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(item.path)}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate(item.path)}
                    styles={{ body: { padding: '12px 16px' } }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontSize: 18, color: BRAND_INDIGO, flexShrink: 0, lineHeight: 1 }}>
                        {item.icon}
                      </span>
                      <Typography.Text strong style={{ fontSize: 14 }}>
                        {item.title}
                      </Typography.Text>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        );
      })()}
    </>
  );
};
